import React from 'react';
import { useSelector } from 'react-redux';
import { useImmerReducer } from 'use-immer';
import eq from 'lodash/eq';
import isEqual from 'lodash/isEqual';
import MultiRef from 'react-multi-ref';
import { usePrevious } from '../lib/customHooks';
import laborcodes from '../lib/laborcodes';
import Autocomplete from '../components/Autocomplete';
import {
	Box,
	Flex,
	Heading,
	HStack,
	Dialog,
	Input,
	Switch,
	Text,
	Textarea,
	Button,
	Accordion,
	CloseButton,
	NumberInput,
	Table,
	NativeSelect,
	Portal,
	Field
} from '@chakra-ui/react';
import { defaultValForFieldType } from '../lib/utils';
import theme from '../theme';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { emailDriverExemptions } from '../lib/email';

import 'react-datepicker/dist/react-datepicker.css';

function reducer(draft, action) {
	switch (action.type) {
		case 'update_field': {
			const payload = Array.isArray(action.payload) ? action.payload : [action.payload];

			payload.forEach(update => {
				const { field, value, index } = update;

				if (index?.parentKey && typeof index?.index === 'number') {
					draft.fields[index.parentKey][index.index][field] = value;
				} else {
					draft.fields[field] = value;
				}
			});

			break;
		}
		case 'add_repeater_set': {
			const { key, entry } = action.payload;

			if (!draft.fields[key]) {
				draft.fields[key] = [];
			}

			draft.fields[key].push(entry);
			break;
		}
		case 'remove_repeater_item': {
			draft.fields[action.payload.index.parentKey].splice(action.payload.index.index, 1);

			break;
		}
		case 'add_time_item': {
			const { code, phase_number, key } = action.payload;
			const codeItem = laborcodes.find(laborCode => laborCode.code === code);

			if (codeItem) {
				// Disable other main time items to enforce only one main item
				if (codeItem.main) {
					draft.fields.time_items.forEach(timeItem => {
						if (timeItem.main) {
							timeItem.main = false;
						}
					});
				}

				draft.fields.time_items.push({
					key,
					phase_number,
					...codeItem
				});
			}

			break;
		}
		case 'remove_time_item': {
			const { index } = action.payload;
			draft.fields.time_items.splice(index, 1);

			break;
		}
		case 'update_time_item': {
			const { index, field, value = null } = action.payload;

			if (field === 'main') {
				draft.fields.time_items.forEach(timeItem => {
					timeItem.main = false;
				});
			}

			if (field === 'onsite' && draft.fields.time_items[index].main === true) {
				draft.fields.time_items[index].main = false;
			}

			draft.fields.time_items[index][field] = value;

			break;
		}
		case 'update_units_field': {
			if (!Object.prototype.hasOwnProperty.call(draft.fields, 'units')) {
				draft.fields.units = {};
			}

			const { field, value } = action.payload;

			if (!Object.prototype.hasOwnProperty.call(draft.fields, 'units')) {
				draft.fields.units = {};
			}

			draft.fields.units[field] = value;

			break;
		}
		case 'update_per_diem_field': {
			const { field, value } = action.payload;
			if (!Object.prototype.hasOwnProperty.call(draft.fields, 'per_diems')) {
				draft.fields.per_diems = {
					phase_number: '',
					task: '',
					overnight: false,
					breakfast_cost: '',
					lunch_cost: '',
					dinner_cost: ''
				};
			}
			if (field === 'phase_number') {
				draft.fields.per_diems.phase_number = value;
				draft.fields.per_diems.task = '';
			} else {
				draft.fields.per_diems[field] = value;
			}

			break;
		}
		case 'add_driver_exemptions': {
			const { driverExemptions } = action.payload;

			if (!draft.fields.driver_exemptions) {
				draft.fields.driver_exemptions = {};
			}

			driverExemptions.forEach(record => {
				if (!draft.fields.driver_exemptions[record.name]) {
					draft.fields.driver_exemptions[record.name] = [];
				}

				draft.fields.driver_exemptions[record.name].push(record);
			});

			break;
		}
		case 'set_time_item_keys': {
			draft.fields.time_items = draft.fields.time_items.map(ti => ({
				key: `${ti.code}-1`,
				...ti
			}));

			break;
		}
		default:
			break;
	}
}

export default ({
	formData,
	formDefinition,
	edit,
	saveForm,
	dataAPI,
	hasDailyPackets,
	currentPacket,
	isSubmitted,
	currentForm
}) => {
	const projectNumber = dataAPI.getPhaseValue('project_number');
	const projectPhases = window.projectList.filter(({ project_number }) => projectNumber === project_number);

	const [state, dispatch] = useImmerReducer(reducer, { fields: formData });

	const updatedFormDefinition = useSelector(state => state.webforms.forms.find(({ id }) => id === formDefinition.id));

	const prevFormId = usePrevious(currentForm.form_id);
	const prevFormNid = usePrevious(currentForm.form_nid);
	const prevPerDiems = usePrevious(state.fields.per_diems);
	const prevUtilityWork = usePrevious(state.fields.utility_work);
	const prevDotDrivers = usePrevious(state.fields.employee_crew_members?.map(({ dot_driver }) => dot_driver));
	const prevDates = usePrevious([state.fields.est_start_date, state.fields.est_end_date]);

	const [removeAutocompleteDialogOpen, setRemoveAutocompleteDialogOpen] = React.useState(null);
	const [removeItemDialogOpen, setRemoveItemDialogOpen] = React.useState(null);
	const [removeLaborCodeDialogOpen, setRemoveLaborCodeDialogOpen] = React.useState(null);
	const [itemRefs] = React.useState(() => new MultiRef());
	const [laborCodeRefs] = React.useState(() => new MultiRef());

	const laborCodeRef = React.useRef(null);

	const updateDefinition = React.useMemo(
		() => !isSubmitted && updatedFormDefinition && !isEqual(updatedFormDefinition, formDefinition),
		[isSubmitted, updatedFormDefinition, formDefinition]
	);

	const isOverhead = React.useMemo(() => state.fields.project_type === 'OVERHEAD', [state.fields.project_type]);
	const employeeCrewMembers = React.useMemo(() => {
		const map = new Map();
		window.employeeList.forEach(employee => map.set(employee.name, employee));
		return map;
	}, []);

	React.useEffect(() => {
		if (!Object.prototype.hasOwnProperty.call(state.fields.time_items[0], 'key')) {
			setTimeout(() => dispatch({ type: 'set_time_item_keys' }), 0);
		}

		const clientName = window.clientList.find(
			({ client_id }) => client_id === dataAPI.getPhaseValue('client_id')
		)?.client_name;

		if (clientName !== state.fields.client_name) {
			setTimeout(
				() =>
					dispatch({
						type: 'update_field',
						payload: { field: 'client_name', value: clientName }
					}),
				0
			);
		}
	}, []);

	React.useEffect(() => {
		if (
			prevFormId === currentForm.form_id &&
			prevFormNid === currentForm.form_nid &&
			currentPacket.packet_tag === 'supervisor' &&
			hasDailyPackets &&
			state.fields.utility_work
		) {
			const dotDrivers = state.fields.employee_crew_members?.map(({ dot_driver }) => dot_driver);
			const dates = [state.fields.est_start_date, state.fields.est_end_date];
			const dotDriversEqual = isEqual(dotDrivers, prevDotDrivers);
			const datesEqual = isEqual(dates, prevDates);

			// prevDotDrivers/prevDates will be undefined on first mount,
			// otherwise, return if dotDrivers are equal or dates are equal
			if (prevDotDrivers && prevDates && (!dotDriversEqual || !datesEqual || !prevUtilityWork)) {
				const timestamp = Math.floor(new Date().getTime() / 1000);

				// get list of employees with DOT Driver enabled
				const driverExemptions = Object.prototype.hasOwnProperty.call(state.fields, 'driver_exemptions')
					? state.fields.employee_crew_members
							?.filter(({ employee_crew_member_name, dot_driver }) => {
								const driverExemptionsLength =
									state.fields.driver_exemptions?.[employee_crew_member_name]?.length;

								// Update driver exemptions only on the following conditions
								return (
									// Check if crew member exists
									employeeCrewMembers.has(employee_crew_member_name) &&
									// Crew member has DOT Driver
									dot_driver &&
									// no previous exemptions exist
									(!driverExemptionsLength ||
										// if so, check if dates were changed with utility work enabled
										!datesEqual ||
										// otherwise, if utility work was previously disabled,
										// check if dates were changed by indexing the last driver exemption
										!isEqual(dates, [
											state.fields.driver_exemptions[employee_crew_member_name]?.[
												driverExemptionsLength - 1
											]?.est_start_date,
											state.fields.driver_exemptions[employee_crew_member_name]?.[
												driverExemptionsLength - 1
											]?.est_end_date
										]))
								);
							})
							?.map(({ employee_crew_member_name }) => {
								const { name, email } = employeeCrewMembers.get(employee_crew_member_name);
								return {
									name,
									email,
									est_start_date: state.fields.est_start_date,
									est_end_date: state.fields.est_end_date,
									marine_solutions_project_manager: state.fields.marine_solutions_project_manager,
									client_name: state.fields.client_name,
									project_steps: state.fields.project_steps,
									timestamp
								};
							})
					: [];

				dispatch({
					type: 'add_driver_exemptions',
					payload: { driverExemptions }
				});

				if (driverExemptions.length) {
					emailDriverExemptions(
						localStorage.getItem('uid'),
						driverExemptions,
						state.fields.project_number + '-' + state.fields.phase_number
					);
				}
			}
		}
	}, [
		prevFormId,
		prevFormNid,
		prevUtilityWork,
		prevDotDrivers,
		prevDates,
		currentPacket,
		currentForm,
		hasDailyPackets,
		state.fields.utility_work,
		state.fields.est_start_date,
		state.fields.est_end_date,
		state.fields.employee_crew_members
	]);

	React.useEffect(() => {
		const dfr = currentPacket.packet_forms.find(packet_form => packet_form.form_id === 'daily_field_record');

		if (
			dfr &&
			state.fields.per_diems &&
			Object.entries(prevPerDiems || {})?.length &&
			!eq(state.fields.per_diems, prevPerDiems)
		) {
			const dfrData = JSON.parse(dfr.form_data);

			let updatedField = Object.entries(prevPerDiems || {}).filter(
				([per_diem, value]) => value !== state.fields.per_diems[per_diem]
			);

			if (updatedField?.length && updatedField[0]?.length) {
				updatedField = updatedField[0][0];
				dfrData.crew_members = dfrData.crew_members.map(crewMember => ({
					...crewMember,
					[updatedField]: !(updatedField === 'phase_number' || updatedField === 'task')
						? state.fields.per_diems[updatedField]
						: crewMember[updatedField]
				}));

				saveForm({ ...dfr, form_data: dfrData });
			}
		}
	}, [state.fields.per_diems, prevPerDiems]);

	React.useEffect(() => {
		if (updateDefinition) {
			saveForm({
				...currentForm,
				form_data: state.fields,
				form_definition: JSON.stringify(updatedFormDefinition)
			});
		} else {
			saveForm({
				...currentForm,
				form_data: state.fields
			});
		}
	}, [state.fields, updateDefinition]);

	function renderFields(field, key, index) {
		const childFields = Object.keys(field).filter(([firstChar]) => firstChar !== '#');
		const type = field['#type'];
		const required = field['#required'] ?? false;
		const disabled = field['#disabled'] ?? false;
		const complete = field['#complete'] ?? '';
		const bind = field['#bind'] ?? false;

		if (complete) {
			const parts = complete.split(':');
			const list = dataAPI.getDataList(parts[0]);
			const autocompleteValue =
				index?.parentKey && typeof index?.index === 'number'
					? state.fields[index.parentKey][index.index][key]
					: state.fields[key];
			const noMatch = complete === 'employees:name' && !employeeCrewMembers.get(autocompleteValue);

			return (
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base" key={key}>
					<Dialog.Root
						role="alertdialog"
						closeOnEscape={false}
						closeOnInteractOutside={false}
						onOpenChange={({ open }) => {
							if (!open) {
								setRemoveAutocompleteDialogOpen(null);
							}
						}}
						open={removeAutocompleteDialogOpen === key + index.index}
						placement="center"
					>
						<Portal>
							<Dialog.Backdrop />
							<Dialog.Positioner>
								<Dialog.Content opacity="1 !important">
									<Dialog.Header>
										<Dialog.Title fontSize="lg" fontWeight="bold">
											Could not find an employee match.
										</Dialog.Title>
									</Dialog.Header>
									<Dialog.Body>
										<p>
											Please check that the name is spelled correctly. We suggest you select a
											match from the autocomplete list below the input. The employee will be
											removed, please try adding again with a correct selection.
										</p>
									</Dialog.Body>
									<Dialog.Footer>
										<Button
											colorPalette="red"
											ml={3}
											bg={theme.colors.red[500]}
											_hover={{ background: theme.colors.red[600] }}
											color={theme.colors.white}
											onClick={() => {
												setRemoveAutocompleteDialogOpen(null);
												dispatch({
													type: 'remove_repeater_item',
													payload: { index }
												});
											}}
											size="lg"
										>
											Remove Item
										</Button>
									</Dialog.Footer>
								</Dialog.Content>
							</Dialog.Positioner>
						</Portal>
					</Dialog.Root>
					<Text as="label" fontSize={16} pb="4px">
						{field['#title']}
					</Text>
					<Field.Root invalid={required && !autocompleteValue}>
						<Autocomplete
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
							dataList={list}
							fieldName={parts[1]}
							value={autocompleteValue}
							onChange={value => {
								dispatch({
									type: 'update_field',
									payload: {
										field: key,
										value,
										index
									}
								});

								if (
									key === 'employee_crew_member_name' &&
									currentPacket.packet_tag === 'supervisor' &&
									state.fields?.[index?.parentKey][index?.index]?.dot_driver
								) {
									dispatch({
										type: 'update_field',
										payload: {
											field: 'dot_driver',
											value: false,
											index
										}
									});
								}
							}}
							onBlur={() => {
								if (autocompleteValue && noMatch) {
									setRemoveAutocompleteDialogOpen(key + index.index);
								}
							}}
							onSelected={value => {
								const reset = [
									...(state.fields?.employee_crew_members ?? []),
									...(state.fields?.non_employee_crew_members ?? []),
									...(state.fields?.crew_members ?? [])
								].find((crewMember, _index) => crewMember[key] === value && _index !== index.index);

								const updates = [{ field: key, value, index }];

								if (bind) {
									const item = list.find(item => item[parts[1]] === value);
									if (item) {
										updates.push({
											field: bind.field,
											value: item[bind.value],
											index
										});
									}
								}

								if (reset) {
									dispatch({
										type: 'remove_repeater_item',
										payload: { index }
									});
								} else {
									dispatch({
										type: 'update_field',
										payload: updates
									});
								}
							}}
						/>
					</Field.Root>
				</Box>
			);
		}

		switch (type) {
			case 'container': {
				return (
					<Box key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}>
						{childFields.map(childField => renderFields(field[childField], childField))}
					</Box>
				);
			}
			case 'webform_section': {
				return (
					<Box key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}>
						<Heading fontSize={24} pt="24px" pb="12px" color={theme.colors.blue[700]} ml="8px">
							{field['#title']}
						</Heading>
						{childFields.map(childField => renderFields(field[childField], childField))}
					</Box>
				);
			}
			case 'fieldset': {
				return (
					<>
						<Accordion.Root
							key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}
							collapsible
							p="8px"
							defaultValue={[key]}
						>
							<Accordion.Item key={key} value={key}>
								<Accordion.ItemTrigger>
									<Accordion.ItemIndicator />
									<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
										{field['#title']}
									</Text>
								</Accordion.ItemTrigger>
								<Accordion.ItemContent p={0}>
									<Accordion.ItemBody>
										{state.fields[key]?.map((_field, _index) => (
											<Flex key={`${_field}_${_index}`} align="flex-start">
												{childFields.map(childField =>
													renderFields(field[childField], childField, {
														parentKey: key,
														index: _index
													})
												)}
											</Flex>
										))}
										{key === 'hospitals' ? (
											<Flex>
												<Box
													w="100%"
													p="8px"
													m="8px"
													bgColor={theme.colors.gray[500]}
													borderRadius="base"
												>
													<Text as="label" fontSize={16} pb="4px">
														On-Call Dive Physician
													</Text>
													<Input
														fontSize={16}
														bgColor={theme.colors.white}
														disabled
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
														value="HM Recompression"
													/>
												</Box>
												<Box
													w="100%"
													p="8px"
													m="8px"
													bgColor={theme.colors.gray[500]}
													borderRadius="base"
												>
													<Text as="label" fontSize={16} pb="4px">
														On-Call Dive Physician Phone Number
													</Text>
													<Input
														fontSize={16}
														bgColor={theme.colors.white}
														disabled
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
														value="832-888-8909"
													/>
												</Box>
											</Flex>
										) : null}
										<Button
											disabled={disabled || (isSubmitted && !edit)}
											m="8px"
											onClick={() => {
												const entry = {};
												childFields.forEach(childField => {
													entry[childField] =
														field[childField]['#default_value'] ??
														defaultValForFieldType(field[childField]['#type']);
												});
												dispatch({
													type: 'add_repeater_set',
													payload: {
														key,
														entry
													}
												});
											}}
											size="lg"
											fontWeight="bold"
										>
											Add another
										</Button>
									</Accordion.ItemBody>
								</Accordion.ItemContent>
							</Accordion.Item>
						</Accordion.Root>
					</>
				);
			}
			case 'webform_flexbox': {
				return (
					<Flex w="100%" key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}>
						{childFields.map(childField =>
							renderFields(field[childField], childField, {
								parentKey: index?.parentKey,
								index: index?.index
							})
						)}
					</Flex>
				);
			}
			case 'textfield': {
				const value =
					index?.parentKey && typeof index?.index === 'number'
						? state.fields[index.parentKey][index.index][key]
						: state.fields[key];

				return (
					<Box
						key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}
						w="100%"
						p="8px"
						m="8px"
						bgColor={theme.colors.gray[500]}
						borderRadius="base"
					>
						<Text as="label" fontSize={16} pb="4px" htmlFor={key}>
							{field['#title']}
						</Text>
						<Field.Root invalid={required && !value}>
							<Input
								fontSize={16}
								bgColor={theme.colors.white}
								name={key}
								id={key}
								value={value}
								onChange={({ target }) =>
									dispatch({
										type: 'update_field',
										payload: {
											field: key,
											value: target.value,
											index
										}
									})
								}
								disabled={disabled || (isSubmitted && !edit)}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
							/>
						</Field.Root>
					</Box>
				);
			}
			case 'textarea': {
				const value =
					index?.parentKey && typeof index?.index === 'number'
						? state.fields[index.parentKey][index.index][key]
						: state.fields[key];

				return (
					<Box
						key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}
						w="100%"
						p="8px"
						m="8px"
						bgColor={theme.colors.gray[500]}
						borderRadius="base"
					>
						<Text as="label" fontSize={16} pb="4px" htmlFor={key}>
							{field['#title']}
						</Text>
						<Field.Root invalid={required && !value}>
							<Textarea
								fontSize={16}
								bgColor={theme.colors.white}
								id={key}
								value={value}
								onChange={({ target }) =>
									dispatch({
										type: 'update_field',
										payload: {
											field: key,
											value: target.value,
											index
										}
									})
								}
								disabled={disabled || (isSubmitted && !edit)}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
							/>
						</Field.Root>
					</Box>
				);
			}
			case 'checkbox': {
				return (
					<HStack
						key={key}
						justify="flex-start"
						align="flex-start"
						alignSelf="stretch"
						width="320px"
						p="8px"
						m="8px"
						bgColor={theme.colors.gray[500]}
						borderRadius="base"
					>
						<Switch.Root
							key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}
							colorPalette="blue"
							size="lg"
							disabled={
								(isOverhead && key === 'mob_only') ||
								(currentPacket.packet_tag !== 'supervisor' &&
									(key === 'utility_work' || key === 'dot_driver')) ||
								(key === 'dot_driver' &&
									// If we're on a supervisor packet, disable DOT Driver toggle if the name is invalid
									(!employeeCrewMembers.get(
										state.fields[index.parentKey][index.index].employee_crew_member_name
									) ||
										// Otherwise, if a valid name is found, check if they autocompleted a duplicate value,
										// and disable DOT Driver for both original and duplicate crew member entries
										state.fields.employee_crew_members.filter(
											({ employee_crew_member_name }) =>
												employee_crew_member_name ===
												state.fields[index.parentKey][index.index].employee_crew_member_name
										).length > 1)) ||
								(isSubmitted && !edit)
							}
							checked={
								isOverhead && key === 'mob_only'
									? false
									: index?.parentKey && typeof index?.index === 'number'
										? state.fields[index.parentKey][index.index][key]
										: state.fields[key]
							}
							onCheckedChange={e =>
								dispatch({
									type: 'update_field',
									payload: {
										field: key,
										value: e.checked,
										index
									}
								})
							}
						>
							<Switch.HiddenInput />
							<Switch.Control />
						</Switch.Root>
						<Text as="label" fontSize={16}>
							{field['#title']}
						</Text>
					</HStack>
				);
			}
			case 'select': {
				const selectOptions = field['#select_options'] ?? false;
				const options = selectOptions
					? dataAPI.getSelectOptions(selectOptions)
					: Object.entries(field['#options'] ?? []).map(([value, label]) => ({
							label,
							value
						}));
				const value =
					index?.parentKey && typeof index?.index === 'number'
						? state.fields[index.parentKey][index.index][key]
						: state.fields[key];

				return (
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base" key={key}>
						<Text as="label" fontSize={16} pb="4px" htmlFor={key}>
							{field['#title']}
						</Text>
						<Field.Root invalid={required && !value}>
							<NativeSelect.Root
								key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}
								size="sm"
							>
								<NativeSelect.Field
									fontSize={16}
									placeholder=" "
									bgColor={theme.colors.white}
									borderRadius="base"
									name={key}
									id={key}
									value={value || ''}
									onChange={({ target }) =>
										dispatch({
											type: 'update_field',
											payload: {
												field: key,
												value: target.value,
												index
											}
										})
									}
									isDisabled={disabled || (isSubmitted && !edit)}
									_disabled={{
										opacity: 1,
										bgColor: theme.colors.gray[400],
										color: theme.colors.black
									}}
								>
									{options.map(({ label, value: _value }, _index) => (
										<option key={_index} value={_value}>
											{label}
										</option>
									))}
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Field.Root>
					</Box>
				);
			}
			case 'date': {
				const value =
					index?.parentKey && typeof index?.index === 'number'
						? state.fields[index.parentKey][index.index][key]
						: state.fields[key];

				return (
					<Box
						key={key + (typeof index?.index === 'number' ? '-' + index.index : '')}
						w="100%"
						p="8px"
						m="8px"
						bgColor={theme.colors.gray[500]}
						borderRadius="base"
					>
						<Text as="label" fontSize={16} pb="4px" htmlFor={key}>
							{field['#title']}
						</Text>
						<DatePicker
							disabled={
								(currentPacket.packet_tag !== 'supervisor' &&
									(key === 'est_start_date' || key === 'est_end_date')) ||
								(isSubmitted && !edit)
							}
							minDate={
								state.fields?.[field['#minimum']] ? state.fields?.[field['#minimum']] * 1000 : null
							}
							maxDate={
								state.fields?.[field['#maximum']] ? state.fields?.[field['#maximum']] * 1000 : null
							}
							selected={value * 1000}
							onChange={date =>
								dispatch({
									type: 'update_field',
									payload: {
										field: key,
										value: moment(date).unix(),
										index
									}
								})
							}
							customInput={
								<Input
									id={key}
									fontSize={16}
									readOnly
									bgColor={theme.colors.white}
									borderColor={required && !value ? 'var(--error-color)' : ''}
									invalid={true}
								/>
							}
						/>
					</Box>
				);
			}
			case 'webform_element': {
				if (key.includes('remove_item') && (!isSubmitted || edit) && !disabled) {
					return (
						<Box>
							<CloseButton
								alignSelf="flex-end"
								size="md"
								m="8px"
								color={theme.colors.white}
								bgColor={theme.colors.red[500]}
								_hover={{ background: theme.colors.red[600] }}
								disabled={isSubmitted && !edit}
								onClick={() => setRemoveItemDialogOpen(key + index.index)}
							/>
							<Dialog.Root
								role="alertdialog"
								initialFocusEl={itemRefs.map.get(key + index.index)}
								onOpenChange={({ open }) => {
									if (!open) {
										setRemoveItemDialogOpen(null);
									}
								}}
								open={removeItemDialogOpen === key + index.index}
								placement="center"
							>
								<Portal>
									<Dialog.Backdrop />
									<Dialog.Positioner>
										<Dialog.Content opacity="1 !important">
											<Dialog.Header>
												<Dialog.Title fontSize="lg" fontWeight="bold">
													Confirm Delete
												</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<p>Are you sure you want to remove this item?</p>
											</Dialog.Body>
											<Dialog.Footer>
												<Dialog.ActionTrigger asChild>
													<Button
														variant="outline"
														size="lg"
														ref={itemRefs.ref(key + index.index)}
														onClick={() => setRemoveItemDialogOpen(null)}
													>
														Cancel
													</Button>
												</Dialog.ActionTrigger>
												<Button
													colorPalette="red"
													size="lg"
													ml={3}
													bg={theme.colors.red[500]}
													_hover={{ background: theme.colors.red[600] }}
													color={theme.colors.white}
													onClick={() => {
														setRemoveItemDialogOpen(null);
														dispatch({
															type: field['#action'],
															payload: { index }
														});
													}}
												>
													Delete Item
												</Button>
											</Dialog.Footer>
											<Dialog.CloseTrigger asChild>
												<CloseButton size="sm" />
											</Dialog.CloseTrigger>
										</Dialog.Content>
									</Dialog.Positioner>
								</Portal>
							</Dialog.Root>
						</Box>
					);
				}

				if (key === 'units_defaults') {
					return (
						<Flex key={key}>
							<Box m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Phase
								</Text>
								<NativeSelect.Root size="sm">
									<NativeSelect.Field
										fontSize={16}
										minW="100px"
										placeholder=" "
										borderRadius="base"
										name={key + '_phase'}
										id={key + '_phase'}
										value={state.fields?.units?.phase_number || ''}
										onChange={({ target }) => {
											dispatch({
												type: 'update_units_field',
												payload: { field: 'phase_number', value: target.value }
											});
											dispatch({
												type: 'update_units_field',
												payload: { field: 'task', value: '' }
											});
										}}
										isDisabled={disabled || (isSubmitted && !edit)}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									>
										{projectPhases.map(({ phase_number }, _index) => (
											<option key={_index} value={phase_number}>
												{phase_number}
											</option>
										))}
									</NativeSelect.Field>
									<NativeSelect.Indicator />
								</NativeSelect.Root>
							</Box>
							<Box w="100%" m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Task
								</Text>
								<Field.Root invalid={!(isOverhead || state.fields?.units?.task || '')}>
									<NativeSelect.Root size="sm">
										<NativeSelect.Field
											fontSize={16}
											placeholder=" "
											borderRadius="base"
											name={key + '_task'}
											id={key + '_task'}
											value={state.fields?.units?.task}
											onChange={({ target }) =>
												dispatch({
													type: 'update_units_field',
													payload: { field: 'task', value: target.value }
												})
											}
											isDisabled={disabled || (isSubmitted && !edit)}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										>
											{(
												projectPhases.find(
													({ phase_number }) =>
														phase_number === state.fields?.units?.phase_number
												)?.tasks || []
											).map(({ name, number }, _index) => (
												<option key={_index} value={number}>
													{name}
												</option>
											))}
										</NativeSelect.Field>
										<NativeSelect.Indicator />
									</NativeSelect.Root>
								</Field.Root>
							</Box>
						</Flex>
					);
				}

				if (key === 'per_diem_defaults') {
					return (
						<Flex key={key}>
							<Box w="100%" m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Phase
								</Text>
								<NativeSelect.Root size="sm">
									<NativeSelect.Field
										fontSize={16}
										placeholder=" "
										borderRadius="base"
										name={key + '_phase'}
										id={key + '_phase'}
										value={state.fields?.per_diems?.phase_number || ''}
										onChange={({ target }) =>
											dispatch({
												type: 'update_per_diem_field',
												payload: { field: 'phase_number', value: target.value }
											})
										}
										isDisabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									>
										{projectPhases.map(({ phase_number }, _index) => (
											<option key={_index} value={phase_number}>
												{phase_number}
											</option>
										))}
									</NativeSelect.Field>
									<NativeSelect.Indicator />
								</NativeSelect.Root>
							</Box>
							<Box w="100%" m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Task
								</Text>
								<Field.Root invalid={!(isOverhead || state.fields?.per_diems?.task)}>
									<NativeSelect.Root size="sm">
										<NativeSelect.Field
											fontSize={16}
											placeholder=" "
											borderRadius="base"
											name={key + '_task'}
											id={key + '_task'}
											value={state.fields?.per_diems?.task || ''}
											onChange={({ target }) =>
												dispatch({
													type: 'update_per_diem_field',
													payload: { field: 'task', value: target.value }
												})
											}
											isDisabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										>
											{(
												projectPhases.find(
													({ phase_number }) =>
														phase_number === state.fields?.per_diems?.phase_number
												)?.tasks || []
											).map(({ name, number }, _index) => (
												<option key={_index} value={number}>
													{name}
												</option>
											))}
										</NativeSelect.Field>
										<NativeSelect.Indicator />
									</NativeSelect.Root>
								</Field.Root>
							</Box>
							<Box m="8px">
								<Text as="label" fontSize={16} pb="4px" whiteSpace="nowrap">
									Overnight Stay?
								</Text>
								<Switch.Root
									size="lg"
									disabled={isSubmitted && !edit}
									checked={state.fields?.per_diems?.overnight}
									onCheckedChange={e =>
										dispatch({
											type: 'update_per_diem_field',
											payload: { field: 'overnight', value: e.checked }
										})
									}
									colorPalette="blue"
								>
									<Switch.HiddenInput />
									<Switch.Control />
								</Switch.Root>
							</Box>
							<Box w="100%" m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Breakfast
								</Text>
								<NumberInput.Root
									precision={2}
									onValueChange={e =>
										dispatch({
											type: 'update_per_diem_field',
											payload: { field: 'breakfast_cost', value: e.value }
										})
									}
									value={state.fields?.per_diems?.breakfast_cost}
									disabled={isSubmitted && !edit}
								>
									<NumberInput.Input
										fontSize={16}
										borderRadius="base"
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</NumberInput.Root>
							</Box>
							<Box w="100%" m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Lunch
								</Text>
								<NumberInput.Root
									precision={2}
									onValueChange={e =>
										dispatch({
											type: 'update_per_diem_field',
											payload: { field: 'lunch_cost', value: e.value }
										})
									}
									value={state.fields?.per_diems?.lunch_cost}
									disabled={isSubmitted && !edit}
								>
									<NumberInput.Input
										fontSize={16}
										borderRadius="base"
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</NumberInput.Root>
							</Box>
							<Box w="100%" m="8px">
								<Text as="label" fontSize={16} pb="4px">
									Dinner
								</Text>
								<NumberInput.Root
									precision={2}
									onValueChange={e =>
										dispatch({
											type: 'update_per_diem_field',
											payload: { field: 'dinner_cost', value: e.value }
										})
									}
									value={state.fields?.per_diems?.dinner_cost}
									disabled={isSubmitted && !edit}
								>
									<NumberInput.Input
										fontSize={16}
										borderRadius="base"
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</NumberInput.Root>
							</Box>
						</Flex>
					);
				}

				if (key === 'time_item_defaults') {
					return (
						<Box p="8px">
							<Table.Root key={key}>
								<Table.Header>
									<Table.Row>
										<Table.ColumnHeader>
											<Text fontSize={16} color={theme.colors.black} fontWeight="bold">
												Labor Code
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text fontSize={16} color={theme.colors.black} fontWeight="bold">
												Phase
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text fontSize={16} color={theme.colors.black} fontWeight="bold">
												Task
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text fontSize={16} color={theme.colors.black} fontWeight="bold">
												Onsite Item
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text fontSize={16} color={theme.colors.black} fontWeight="bold">
												Onsite Main
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader />
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{state.fields.time_items.map((timeItem, _index) => (
										<Table.Row key={`${timeItem.code}_${_index}`}>
											<Table.Cell>
												<Text fontSize={16}>
													{timeItem.code} - {timeItem.label}
												</Text>
											</Table.Cell>
											<Table.Cell>
												<NativeSelect.Root size="sm">
													<NativeSelect.Field
														fontSize={16}
														placeholder=" "
														borderRadius="base"
														value={timeItem.phase_number || ''}
														onChange={({ target }) => {
															dispatch({
																type: 'update_time_item',
																payload: {
																	index: _index,
																	field: 'phase_number',
																	value: target.value
																}
															});
															dispatch({
																type: 'update_time_item',
																payload: { index: _index, field: 'task', value: '' }
															});
														}}
														isDisabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													>
														{projectPhases.map(({ phase_number }, _index) => (
															<option key={_index} value={phase_number}>
																{phase_number}
															</option>
														))}
													</NativeSelect.Field>
													<NativeSelect.Indicator />
												</NativeSelect.Root>
											</Table.Cell>
											<Table.Cell>
												<Field.Root
													invalid={!(timeItem.task || isOverhead || state.fields.mob_only)}
												>
													<NativeSelect.Root size="sm">
														<NativeSelect.Field
															fontSize={16}
															placeholder=" "
															borderRadius="base"
															value={timeItem.task || ''}
															onChange={({ target }) => {
																dispatch({
																	type: 'update_time_item',
																	payload: {
																		index: _index,
																		field: 'task',
																		value: target.value
																	}
																});
															}}
															isDisabled={isSubmitted && !edit}
															_disabled={{
																opacity: 1,
																bgColor: theme.colors.gray[400],
																color: theme.colors.black
															}}
														>
															{(
																projectPhases.find(
																	({ phase_number }) =>
																		phase_number === timeItem.phase_number
																)?.tasks || []
															).map(({ name, number }, _index) => (
																<option key={_index} value={number}>
																	{name}
																</option>
															))}
														</NativeSelect.Field>
														<NativeSelect.Indicator />
													</NativeSelect.Root>
												</Field.Root>
											</Table.Cell>
											<Table.Cell>
												<Switch.Root
													size="lg"
													checked={timeItem.onsite}
													disabled={isSubmitted && !edit}
													onCheckedChange={e =>
														dispatch({
															type: 'update_time_item',
															payload: {
																index: _index,
																field: 'onsite',
																value: e.checked
															}
														})
													}
													colorPalette="blue"
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
											</Table.Cell>
											<Table.Cell>
												<Switch.Root
													size="lg"
													disabled={(isSubmitted && !edit) || !timeItem.onsite}
													checked={timeItem.main}
													onCheckedChange={e => {
														{
															if (
																timeItem.code === 103 ||
																timeItem.code === 112 ||
																timeItem.code === 131 ||
																timeItem.code === 403 ||
																timeItem.code === 603
															) {
																dispatch({
																	type: 'update_time_item',
																	payload: {
																		index: _index,
																		field: 'main',
																		value: e.checked
																	}
																});
															}
														}
													}}
													colorPalette="blue"
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
											</Table.Cell>
											{/* Per Amy:
												Let’s just start with not allowing 110 to be deleted.
												That should help fix this for now and possibly forever.
												I have thoughts on some different ways to make it work, but maybe we just do this and see how we do.
											*/}
											{(!isSubmitted || edit) &&
												state.fields.time_items[_index].key !== '110-1' && (
													<Table.Cell>
														<CloseButton
															size="md"
															color={theme.colors.white}
															bgColor={theme.colors.red[500]}
															_hover={{ background: theme.colors.red[600] }}
															disabled={isSubmitted && !edit}
															onClick={() => setRemoveLaborCodeDialogOpen(_index)}
														/>
														<Dialog.Root
															role="alertdialog"
															initialFocusEl={laborCodeRefs.map.get(_index)}
															onOpenChange={({ open }) => {
																if (!open) {
																	setRemoveLaborCodeDialogOpen(null);
																}
															}}
															open={removeLaborCodeDialogOpen === _index}
															placement="center"
														>
															<Portal>
																<Dialog.Backdrop />
																<Dialog.Positioner>
																	<Dialog.Content opacity="1 !important">
																		<Dialog.Header>
																			<Dialog.Title
																				fontSize="lg"
																				fontWeight="bold"
																			>
																				Confirm Delete
																			</Dialog.Title>
																		</Dialog.Header>
																		<Dialog.Body>
																			<p>
																				Are you sure you want to remove this
																				item?
																			</p>
																		</Dialog.Body>
																		<Dialog.Footer>
																			<Dialog.ActionTrigger asChild>
																				<Button
																					variant="outline"
																					size="lg"
																					ref={laborCodeRefs.ref(_index)}
																					onClick={() =>
																						setRemoveLaborCodeDialogOpen(
																							null
																						)
																					}
																				>
																					Cancel
																				</Button>
																			</Dialog.ActionTrigger>
																			<Button
																				colorPalette="red"
																				size="lg"
																				ml={3}
																				_hover={{
																					background: theme.colors.red[600]
																				}}
																				color={theme.colors.white}
																				onClick={() => {
																					setRemoveLaborCodeDialogOpen(null);
																					dispatch({
																						type: 'remove_time_item',
																						payload: { index: _index }
																					});
																				}}
																			>
																				Delete Item
																			</Button>
																		</Dialog.Footer>
																		<Dialog.CloseTrigger asChild>
																			<CloseButton size="sm" />
																		</Dialog.CloseTrigger>
																	</Dialog.Content>
																</Dialog.Positioner>
															</Portal>
														</Dialog.Root>
													</Table.Cell>
												)}
										</Table.Row>
									))}
								</Table.Body>
							</Table.Root>
							<Flex align="flex-end">
								<Box m="8px">
									<Text as="label" fontSize={16} pb="4px">
										Labor Codes
									</Text>
									<NativeSelect.Root size="sm">
										<NativeSelect.Field
											fontSize={16}
											placeholder=" "
											ref={laborCodeRef}
											borderRadius="base"
											isDisabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										>
											{/*Duplicates are allowed here*/}
											{laborcodes.map(({ label, code }, _index) => (
												<option key={_index} value={code}>
													{`${code} - ${label}`}
												</option>
											))}
										</NativeSelect.Field>
										<NativeSelect.Indicator />
									</NativeSelect.Root>
								</Box>
								<Button
									size="lg"
									m="8px"
									onClick={() => {
										if (laborCodeRef.current.value) {
											const existingCodesLength = state.fields.time_items.filter(
												({ code }) => code === parseInt(laborCodeRef.current.value)
											).length;

											// Key is for determining duplicate time codes
											dispatch({
												type: 'add_time_item',
												payload: {
													key: `${laborCodeRef.current.value}-${existingCodesLength + 1}`,
													code: parseInt(laborCodeRef.current.value),
													phase_number: formData.phase_number
												}
											});
										}
									}}
									fontWeight="bold"
									disabled={isSubmitted && !edit}
								>
									Add Labor Code
								</Button>
							</Flex>
						</Box>
					);
				}

				break;
			}
			default:
				break;
		}
	}

	return renderFields(formDefinition.elements.form_root);
};
